/**
 * Room Selection Thunks
 *
 * Handles room selection and navigation to booking review.
 * Uses new MCP format for HOTEL_REVIEW_MCP API.
 *
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { HotelBookingReviewMCPRequest } from '@contracts';
import { fetchHotelBookingReview } from '../api';
import { getCBContext } from '../api/booking-review/baseApi';
import type { RootState } from '@/store/store';
import { setActiveTemplate } from '@/features/chat/redux';
import { TEMPLATE_TYPES } from '@/features/chat/constants/templateTypes';
import { getOrCreateSessionId, getUUID, generateUUID } from '@/shared/utils';
import { selectDetailsSearchContext } from '../selectors/hotelConfigSelectors';
import { TRACKING_DATA, fireTrackingEvents } from '@shared/analytics';

// ============================================
// Types
// ============================================

export interface SelectRoomParams {
  readonly ratePlanCode: string;
  readonly roomCode: string;
}

export interface RoomCriterion {
  readonly ratePlanCode: string;
  readonly roomCode: string;
  readonly mtKey: string;
  readonly pricingKey?: string;
  readonly roomStayCandidates: Array<{
    readonly adultCount: number;
    readonly rooms: number;
    readonly childAges: readonly number[];
  }>;
}

export interface SelectOccupancyRoomsParams {
  readonly roomCriteria: readonly RoomCriterion[];
  readonly searchType: 'O' | 'R'; // O for occupancy, R for combo
  readonly pricingKey?: string; // Required for occupancy (searchType: O)
}

// ============================================
// Thunks
// ============================================

/**
 * Handle occupancy/combo room selection and navigate to booking review
 * Uses new MCP format for HOTEL_REVIEW_MCP API
 *
 * Uses detailsSearchContext (from details API response) for dates and pax info.
 */
export const selectOccupancyRoomsAndNavigateThunk = createAsyncThunk<
  void,
  SelectOccupancyRoomsParams,
  { state: RootState }
>('hotels/selectOccupancyRoomsAndNavigate', async (params, { getState, dispatch }) => {
  const state = getState();
  const searchContext = state.hotels.searchContext.searchContext;
  const detailsSearchContext = selectDetailsSearchContext(state);
  const hotelName = state.hotels.details.data?.header.name ?? '';

  // Extract dates from details search context (from details API response)
  const dates = detailsSearchContext?.['dates'] as
    | { checkin?: string; checkout?: string }
    | undefined;
  const checkIn = dates?.checkin ?? searchContext.checkIn ?? '';
  const checkOut = dates?.checkout ?? searchContext.checkOut ?? '';

  // Validate required search context
  if (!searchContext.hotelId) {
    throw new Error('Hotel ID is required to proceed with booking');
  }

  // Validate pricingKey for occupancy rooms
  if (params.searchType === 'O' && !params.pricingKey) {
    throw new Error('Pricing key is required for occupancy room selection');
  }

  // Get context for device info and auth
  const context = getCBContext();

  // Build MCP format roomCriteria - map each criterion with its roomStayCandidates
  const mcpRoomCriteria = params.roomCriteria.map((criterion) => ({
    ratePlanCode: criterion.ratePlanCode,
    roomCode: criterion.roomCode,
    adultCount: criterion.roomStayCandidates[0]?.adultCount ?? 1,
    rooms: criterion.roomStayCandidates[0]?.rooms,
    childAges: criterion.roomStayCandidates[0]?.childAges
      ? [...criterion.roomStayCandidates[0].childAges]
      : undefined,
  }));

  // Calculate totals from roomStayCandidates
  // Note: adultCount in roomStayCandidates is already the TOTAL adults for that candidate
  // (accumulated when building criteria), so we should NOT multiply by rooms again
  const totalAdults = params.roomCriteria.reduce((sum, criterion) => {
    return (
      sum +
      criterion.roomStayCandidates.reduce(
        (candidateSum, candidate) => candidateSum + candidate.adultCount,
        0,
      )
    );
  }, 0);
  const totalRooms = params.roomCriteria.reduce((sum, criterion) => {
    return (
      sum +
      criterion.roomStayCandidates.reduce(
        (candidateSum, candidate) => candidateSum + candidate.rooms,
        0,
      )
    );
  }, 0);

  // Build MCP format payload directly
  const payload: HotelBookingReviewMCPRequest = {
    request_id: generateUUID(),
    conversation_id: getOrCreateSessionId(),
    uuid: getUUID(),
    auth: context.authToken,
    device_info: {
      device_id: context.deviceId,
      app_ver: '1.21.1',
      device_type: 'DESKTOP',
      platform: 'DESKTOP',
    },
    rephrased_query: `Show me review page for hotel ${hotelName}`,
    intents: ['Review'],
    action_type: 'Avail-Room',
    location_context: {
      hotel: hotelName,
      hotel_id: searchContext.hotelId,
      city_code: searchContext.cityCode ?? undefined,
    },
    checkin: checkIn,
    checkout: checkOut,
    adult_count: totalAdults,
    child_count: searchContext.roomStayCandidates.childAges.length,
    room_count: totalRooms,
    children_ages: [...searchContext.roomStayCandidates.childAges],
    currency: searchContext.currency ?? context.currency ?? 'INR',
    roomCriteria: mcpRoomCriteria,
    logged_in: context.authToken !== '',
    funnelSource: 'HOTELS',
    page: 'REVIEW',
  };

  // Build booking review request
  const bookingReviewRequest = {
    sessionId: getOrCreateSessionId(),
    userId: getUUID(),
    payload,
  };

  const response = await fetchHotelBookingReview(bookingReviewRequest);

  if (!response.success || !response.data) {
    const errorMessage = response.error?.message ?? 'Failed to fetch booking review';
    const errorCode = response.error?.code;
    const error = new Error(errorMessage) as Error & { code?: string };
    if (errorCode !== undefined) error.code = errorCode;
    throw error;
  }

  // Navigate to review page with API response
  const trackingInfo = TRACKING_DATA[TEMPLATE_TYPES.HOTEL_REVIEW] ?? null;
  dispatch(
    setActiveTemplate({
      template: TEMPLATE_TYPES.HOTEL_REVIEW,
      data: response.data,
      trackingInfo,
    }),
  );
  fireTrackingEvents(trackingInfo);
});

/**
 * Handle room selection and navigate to booking review
 * Uses new MCP format for HOTEL_REVIEW_MCP API
 *
 * Uses detailsSearchContext (from details API response) for dates and pax info.
 */
export const selectRoomAndNavigateThunk = createAsyncThunk<
  void,
  SelectRoomParams,
  { state: RootState }
>('hotels/selectRoomAndNavigate', async (params, { getState, dispatch }) => {
  const state = getState();
  const searchContext = state.hotels.searchContext.searchContext;
  const detailsSearchContext = selectDetailsSearchContext(state);
  const hotelName = state.hotels.details.data?.header.name ?? '';

  // Extract dates from details search context (from details API response)
  const dates = detailsSearchContext?.['dates'] as
    | { checkin?: string; checkout?: string }
    | undefined;
  const checkIn = dates?.checkin ?? searchContext.checkIn ?? '';
  const checkOut = dates?.checkout ?? searchContext.checkOut ?? '';

  // Extract paxCount from details search context with fallback to structured searchContext
  const paxCount = detailsSearchContext?.['paxCount'] as
    | { adults?: number; rooms?: number; children?: number }
    | undefined;
  const adultCount = paxCount?.adults ?? searchContext.roomStayCandidates.adultCount;
  const rooms = paxCount?.rooms ?? searchContext.roomStayCandidates.rooms;

  // Validate required search context
  if (!searchContext.hotelId) {
    throw new Error('Hotel ID is required to proceed with booking');
  }

  // Get context for device info and auth
  const context = getCBContext();

  // Build MCP format payload directly
  const payload: HotelBookingReviewMCPRequest = {
    request_id: generateUUID(),
    conversation_id: getOrCreateSessionId(),
    uuid: getUUID(),
    auth: context.authToken,
    device_info: {
      device_id: context.deviceId,
      app_ver: '1.21.1',
      device_type: 'DESKTOP',
      platform: 'DESKTOP',
    },
    rephrased_query: `Show me review page for hotel ${hotelName}`,
    intents: ['Review'],
    action_type: 'Avail-Room',
    location_context: {
      hotel: hotelName,
      hotel_id: searchContext.hotelId,
      city_code: searchContext.cityCode ?? undefined,
    },
    checkin: checkIn,
    checkout: checkOut,
    adult_count: adultCount,
    child_count: searchContext.roomStayCandidates.childAges.length,
    room_count: rooms,
    children_ages: [...searchContext.roomStayCandidates.childAges],
    currency: searchContext.currency ?? context.currency ?? 'INR',
    roomCriteria: [
      {
        ratePlanCode: params.ratePlanCode,
        roomCode: params.roomCode,
        adultCount,
        rooms,
        childAges: [...searchContext.roomStayCandidates.childAges],
      },
    ],
    logged_in: context.authToken !== '',
    funnelSource: 'HOTELS',
    page: 'REVIEW',
  };

  // Build booking review request
  const bookingReviewRequest = {
    sessionId: getOrCreateSessionId(),
    userId: getUUID(),
    payload,
  };

  const response = await fetchHotelBookingReview(bookingReviewRequest);

  if (!response.success || !response.data) {
    const errorMessage = response.error?.message ?? 'Failed to fetch booking review';
    const errorCode = response.error?.code;
    const error = new Error(errorMessage) as Error & { code?: string };
    if (errorCode !== undefined) error.code = errorCode;
    throw error;
  }

  // Navigate to review page with API response
  const reviewTracking = TRACKING_DATA[TEMPLATE_TYPES.HOTEL_REVIEW] ?? null;
  dispatch(
    setActiveTemplate({
      template: TEMPLATE_TYPES.HOTEL_REVIEW,
      data: response.data,
      trackingInfo: reviewTracking,
    }),
  );
  fireTrackingEvents(reviewTracking);
});
